import { NurseLayout } from "@/components/layout/nurse-layout";
import { Link } from "wouter";
import {
  TrendingUp, Award, Stethoscope, GraduationCap, Users, BookOpenCheck, ChevronRight, Briefcase,
} from "lucide-react";

const CAREER_CARDS = [
  {
    href: "/nurse/career/band-progression",
    icon: TrendingUp,
    bg: "bg-teal-50",
    iconColor: "text-teal-700",
    hoverBorder: "hover:border-teal-300",
    hoverTitle: "group-hover:text-teal-700",
    title: "NHS Band Progression",
    desc: "Band 5 to Consultant Nurse — salary bands, progression milestones, portfolio requirements, and practical advice for each step on the NHS Agenda for Change framework.",
    tags: ["Band 5–8c", "AfC", "Job Planning", "PDR"],
  },
  {
    href: "/nurse/career/nmc-standards",
    icon: Award,
    bg: "bg-indigo-50",
    iconColor: "text-indigo-700",
    hoverBorder: "hover:border-indigo-300",
    hoverTitle: "group-hover:text-indigo-700",
    title: "NMC Standards & Revalidation",
    desc: "NMC Code of Conduct, PREP standards, three-year revalidation requirements, and how to build a compliant reflection and confirmation portfolio.",
    tags: ["NMC Code", "Revalidation", "PREP", "Reflection"],
  },
  {
    href: "/nurse/career/anp-acp",
    icon: Stethoscope,
    bg: "bg-blue-50",
    iconColor: "text-blue-700",
    hoverBorder: "hover:border-blue-300",
    hoverTitle: "group-hover:text-blue-700",
    title: "Advanced Practice & Prescribing",
    desc: "ANP, ACP, and Independent Nurse Prescribing (V300) routes — UCAS/HEI entry requirements, credential frameworks (NHS England ACP), and role expansion in rheumatology.",
    tags: ["ANP", "ACP", "V300", "Independent Prescribing"],
  },
  {
    href: "/nurse/career/rcn-specialist",
    icon: BookOpenCheck,
    bg: "bg-rose-50",
    iconColor: "text-rose-700",
    hoverBorder: "hover:border-rose-300",
    hoverTitle: "group-hover:text-rose-700",
    title: "RCN / BSR Specialist Certification",
    desc: "RCN Rheumatology Nursing Forum membership, BSR rheumatology nursing credentials, EULAR courses, and specialist nurse certification pathways.",
    tags: ["RCN", "BSR", "EULAR", "CNS Certification"],
  },
  {
    href: "/nurse/career/research",
    icon: GraduationCap,
    bg: "bg-amber-50",
    iconColor: "text-amber-700",
    hoverBorder: "hover:border-amber-300",
    hoverTitle: "group-hover:text-amber-700",
    title: "Academic & Research Pathway",
    desc: "Clinical academic roles, NIHR funding streams, PhD and MSc routes for nurses, and how to combine research with clinical practice in rheumatology.",
    tags: ["NIHR", "PhD / MSc", "Clinical Academic", "Research"],
  },
  {
    href: "/nurse/career/leadership",
    icon: Users,
    bg: "bg-purple-50",
    iconColor: "text-purple-700",
    hoverBorder: "hover:border-purple-300",
    hoverTitle: "group-hover:text-purple-700",
    title: "Nursing Leadership",
    desc: "Lead Nurse and Matron pathways, NHS leadership programmes (GMTS, FMLM, Florence Nightingale Foundation), and building a leadership portfolio alongside clinical practice.",
    tags: ["Lead Nurse", "Matron", "GMTS", "FNF Fellowship"],
  },
];

export default function NurseCareerHub() {
  return (
    <NurseLayout title="Career Pathway">
      <div className="space-y-8">

        {/* Hero */}
        <div className="bg-gradient-to-br from-teal-900 to-teal-950 rounded-2xl p-8 text-white">
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-teal-800/60 p-3 rounded-xl">
              <Briefcase className="h-8 w-8 text-teal-300" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Rheumatology Nursing Career Pathways</h2>
              <p className="text-teal-300 text-sm mt-0.5">UK NHS · NMC Registered · Specialist & Advanced Practice</p>
            </div>
          </div>
          <p className="text-teal-200 text-sm leading-relaxed max-w-3xl">
            Whether you are a newly qualified Band 5 starting your rheumatology career or an experienced Clinical Nurse Specialist aiming for advanced practice or a Consultant Nurse role, this hub provides structured guidance for each step — covering NHS banding, NMC standards, advanced clinical practice routes, specialist certification, research, and leadership.
          </p>
        </div>

        {/* Career cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {CAREER_CARDS.map((card) => (
            <Link key={card.href} href={card.href}>
              <div className={`bg-white rounded-2xl border border-slate-200 p-6 ${card.hoverBorder} hover:shadow-md transition-all cursor-pointer group h-full flex flex-col`}>
                <div className={`${card.bg} ${card.iconColor} h-12 w-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-105 transition-transform shrink-0`}>
                  <card.icon className="h-6 w-6" />
                </div>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className={`font-bold text-slate-900 ${card.hoverTitle} transition-colors leading-snug`}>{card.title}</h3>
                  <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-teal-500 shrink-0 mt-0.5 transition-colors" />
                </div>
                <p className="text-sm text-slate-500 leading-relaxed flex-1">{card.desc}</p>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {card.tags.map(t => (
                    <span key={t} className="text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{t}</span>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* NMC quick link */}
        <a href="https://www.nmc.org.uk" target="_blank" rel="noopener noreferrer">
          <div className="bg-gradient-to-r from-indigo-50 to-teal-50 border border-indigo-200 rounded-2xl p-5 hover:shadow-md transition-all cursor-pointer group">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
                <Award className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-slate-900 group-hover:text-indigo-700 transition-colors">NMC Register & Revalidation Portal</h3>
                <p className="text-sm text-slate-500 mt-0.5">nmc.org.uk — check registration status, submit revalidation, and access NMC guidance</p>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-indigo-500 shrink-0 transition-colors" />
            </div>
          </div>
        </a>

      </div>
    </NurseLayout>
  );
}
